// TODO(remove-require-phone-number): unreachable after `require_phone_number` flag removal — delete in follow-up PR. See cleanup-require-phone-number-deadcode.md.
import {
  breakpointSizes,
  getBreakpointDown,
  getFontCss,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import Button, {
  ButtonDesigns,
  ButtonTypes,
} from 'src/components/Button/Button'
import ErrorList from 'src/components/ErrorList/ErrorList'
import FieldLabel from 'src/components/FieldLabel/FieldLabel'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import Popup from 'src/components/Popup/Popup'
import TextInputField, {
  TextInputFieldDesigns,
} from 'src/components/TextInputField/TextInputField'
import { ImageNames } from 'src/enums'
import { useBackgroundDispatch, useFormFields } from 'src/hooks'
import { useColorOrGrayscaleImage } from 'src/hooks/brandHooks'
import Trns, { translateSingleString } from 'src/localization/Trns'
import { profileInfoUrl } from 'src/navigation/urls'
import {
  createSetIsUpdatePhoneNumberPopupOpen,
  createSetIsVerifyPhoneNumberPopupOpen,
} from 'src/redux/actions/app.actions'
import {
  getMe,
  postAuthPhoneVerification,
  postAuthSendPhoneVerificationCode,
  resetVerificationErrors,
} from 'src/redux/actions/auth.actions'
import {
  selectEmailFromState,
  selectNullSafeLocaleFromState,
  selectResendVerificationTopLevelErrorFromState,
  selectUserListModelFromState,
  selectVerificationPhoneResentFromState,
  selectVerifyPhoneTopLevelErrorFromState,
  selectVerifyPhoneVerificationLoadingFromState,
} from 'src/redux/selectors/auth.selectors'
import { selectLocationFromState } from 'src/redux/selectors/router.selectors'
import { formatPhoneNumber } from 'src/utils/string.utils'
import { stringIsEmpty } from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

type PhoneNumberVerificationFlatFields = {
  verificationCode?: string
}

interface VerifyPhoneNumberProps {
  className?: string
  phoneNumber?: string
  password?: string
  phoneNumberProfileSettingsFlow?: boolean
}

const VerifyPhoneNumberPopup: React.FC<VerifyPhoneNumberProps> = (props) => {
  const dispatch = useBackgroundDispatch()
  const user = useSelector(selectUserListModelFromState)
  const locale = useSelector(selectNullSafeLocaleFromState)
  const resendVerificationTopLevelError = useSelector(
    selectResendVerificationTopLevelErrorFromState,
  )
  const verifyPhoneTopLevelError = useSelector(
    selectVerifyPhoneTopLevelErrorFromState,
  )
  const verifyPhoneVerificationLoading = useSelector(
    selectVerifyPhoneVerificationLoadingFromState,
  )
  const verificationPhoneResent = useSelector(
    selectVerificationPhoneResentFromState,
  )
  const email = user?.email ? user.email : useSelector(selectEmailFromState)
  const location = useSelector(selectLocationFromState)

  const addPhoneNumberFlow = location.pathname.includes(profileInfoUrl())
  const buildingsImageSrc = useColorOrGrayscaleImage(ImageNames.Buildings)

  useEffect(() => {
    dispatch(getMe())
  }, [])

  const {
    fields: phoneNumberVerificationFlatFields,
    fieldsErrors: phoneNumberVerificationFlatFieldsErrors,
    onFieldChange: onPhoneNumberVerificationFlatFieldChange,
    setErrorsAndReturnDidPassValidation:
      phoneNumberVerificationFlatFieldsSetErrors,
  } = useFormFields<PhoneNumberVerificationFlatFields>(
    {},
    () => ({
      verificationCode: [stringIsEmpty()],
    }),
    undefined,
  )

  const dispatchPostPhoneVerification = (): void => {
    const code = phoneNumberVerificationFlatFields.verificationCode
    const phoneNumber = props.phoneNumber ?? user?.phone
    if (phoneNumberVerificationFlatFieldsSetErrors() && code) {
      if (props.phoneNumberProfileSettingsFlow) {
        dispatch(postAuthPhoneVerification(email, code, false, phoneNumber))
      } else {
        dispatch(postAuthPhoneVerification(email, code, true, phoneNumber))
      }
    }
  }

  const dispatchPostResendPhoneVerificationCode = (val: string): void => {
    dispatch(postAuthSendPhoneVerificationCode(val, user?.phone, true))
  }

  return (
    <Popup
      noPadding
      onClose={(): void => {
        dispatch(createSetIsVerifyPhoneNumberPopupOpen(false))
        dispatch(createSetIsUpdatePhoneNumberPopupOpen(false))
        dispatch(resetVerificationErrors())
      }}
    >
      <div
        css={css`
          position: relative;
          padding: 60px;
        `}
      >
        <img
          src={buildingsImageSrc}
          alt=""
          css={css`
            position: absolute;
            top: 0;
            right: 0;
            border-top-right-radius: 10px;
            ${getBreakpointDown(breakpointSizes.XSM)(css`
              display: none;
            `)}
          `}
        />
        <div
          css={css`
            display: flex;
            flex-direction: column;
            ${getFontCss(
              32,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.medium,
            )};
            margin-bottom: 20px;
          `}
        >
          <Trns
            source={
              props.phoneNumberProfileSettingsFlow && !addPhoneNumberFlow
                ? 'Update phone number'
                : addPhoneNumberFlow
                  ? 'Add a phone number'
                  : 'Verify phone number'
            }
          />
        </div>
        <div
          css={css`
            ${getFontCss(
              16,
              themeConstants.colors.font_primary,
              styleConstants.fontFamilies.regular,
            )};
          `}
        >
          <div>
            <Trns
              source={
                "Please confirm the 6-digit verification code we sent you. If you don't see it, click the “Resend code” button."
              }
            />
          </div>
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: column;
          `}
        >
          <FieldLabel
            css={css`
              margin-top: 40px;
            `}
          >
            <Trns source={'Phone Number'} />
          </FieldLabel>
          {formatPhoneNumber(props.phoneNumber ?? user?.phone)}
          <TextInputField
            css={css`
              margin-top: 20px;
            `}
            fieldLabel={
              <div
                css={css`
                  display: flex;
                  justify-content: space-between;
                  align-items: flex-end;
                  width: 100%;
                `}
              >
                <Trns source={'Verification Code'} />
                <HoverableLink
                  underline
                  regular
                  size={HoverableLinkSizes.Medium}
                  ariaLabelSource={'Resend code'}
                  onClick={(): void => {
                    if (email) {
                      dispatchPostResendPhoneVerificationCode(email)
                    }
                  }}
                  disabled={verificationPhoneResent}
                >
                  <Trns source={'Resend code'} />
                </HoverableLink>
              </div>
            }
            placeholder={translateSingleString(locale, 'Enter code')}
            onChange={(value: string): void =>
              onPhoneNumberVerificationFlatFieldChange(
                'verificationCode',
                value,
              )
            }
            design={TextInputFieldDesigns.New}
            errors={phoneNumberVerificationFlatFieldsErrors['verificationCode']}
          />
          {resendVerificationTopLevelError && (
            <ErrorList
              errors={[resendVerificationTopLevelError]}
              css={css`
                margin-top: 8px;
              `}
            />
          )}
          <div
            css={css`
              margin-top: 40px;
              display: flex;
              gap: 20px;
              align-items: center;
            `}
          >
            <Button
              css={css`
                height: 40px;
              `}
              type={ButtonTypes.button}
              design={ButtonDesigns.designCta}
              big={true}
              text={<Trns source={'Verify Code'} />}
              onClick={(): void => {
                dispatchPostPhoneVerification()
              }}
              loading={verifyPhoneVerificationLoading}
            />
            <HoverableLink
              ariaLabelSource={'Cancel'}
              onClick={(): void => {
                dispatch(createSetIsVerifyPhoneNumberPopupOpen(false))
                dispatch(createSetIsUpdatePhoneNumberPopupOpen(false))
                dispatch(resetVerificationErrors())
              }}
            >
              <Trns source={'Cancel'} />
            </HoverableLink>
          </div>
          {verifyPhoneTopLevelError && (
            <ErrorList
              errors={[verifyPhoneTopLevelError]}
              css={css`
                margin-top: 8px;
              `}
            />
          )}
        </div>
      </div>
    </Popup>
  )
}

export default VerifyPhoneNumberPopup
